import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCashRegister,
  faReceipt,
  faBoxesStacked,
  faChartLine,
  faUsers,
  faLayerGroup,
  faEye,
  faChartSimple,
  faCheckCircle,
  faClock,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
  "../images/react.png",
  "../images/azure.svg",
  "../images/azure-sql.png",
  "../images/nodejs.png",
  "../images/csharp.png",
];

const PosSystem = () => {
  return (
    <>
      <Helmet>
        <title>Bell POS Case Study | POS System Software by Data Repo</title>
        <meta
          name="description"
          content="See how Data Repo developed Bell POS, a point of sale system that helps retail and restaurant businesses manage sales, inventory, billing, and reporting efficiently."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
        <h1 className="text-3xl md:text-6xl font-bold uppercase">Bell POS</h1>
        <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
          Point of Sale System Developed by Data Repo
        </p>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Modernizing Retail & Restaurant Operations</h2>
            <p className="text-lg text-gray-700 mb-4">
              Many retail stores and restaurants rely on outdated billing systems or manual processes,
              which can lead to slow checkouts, inventory errors, and limited sales visibility.
            </p>
            <p className="text-lg text-gray-700">
              Bell POS was developed by Data Repo to provide a reliable, easy-to-use point of sale system
              that helps businesses manage transactions, inventory, and reporting from one platform.
            </p>
          </div>
          <img src="../images/bell-pos-inventory.png" alt="POS dashboard overview" className="rounded-xl shadow-lg" />
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Inside the Bell POS Dashboard</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Bell POS provides business owners with real-time insights into sales,
            inventory, and transactions through a modern, easy-to-use interface.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/bell-pos-purchase-orders.png" className="rounded-xl shadow-lg" alt="bell pos" />
          <img src="../images/bell-pos-invoice-totals.png" className="rounded-xl shadow-lg" alt="bell pos" />
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
              <p className="text-lg font-semibold text-gray-800">
                Bell POS is helping businesses manage sales and inventory efficiently
              </p>
            </div>
          </div>

          <a
            href="https://bellpos.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
          >
            Visit Website →
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            Bell POS was built to simplify billing, improve inventory accuracy, and give
            business owners clear visibility into their daily sales performance.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Centralized Sales Management</h3>
              <p>Handle all transactions through a single, reliable POS interface.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Operational Visibility</h3>
              <p>Give business owners real-time insights into sales and stock levels.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartSimple} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Scalable Solution</h3>
              <p>Support single stores and multi-branch businesses with one system.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="../images/pos-manual.jpg" alt="Manual POS system" className="rounded-xl shadow-md" />
          <div>
            <h2 className="text-3xl font-bold mb-6">Business Challenges</h2>
            <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
              <li>Slow checkout processes</li>
              <li>Inventory mismatches and stock errors</li>
              <li>Lack of detailed sales reports</li>
              <li>Difficulty managing multiple store locations</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <FontAwesomeIcon icon={faCashRegister} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sales Processing</h3>
            <p>Fast and accurate billing for retail and restaurant transactions.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faReceipt} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Invoice & Receipts</h3>
            <p>Generate digital and printed receipts for every transaction.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faBoxesStacked} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Inventory Management</h3>
            <p>Track stock levels and product movement in real time.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faUsers} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">User & Staff Management</h3>
            <p>Assign roles and monitor employee sales activity.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChartLine} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sales Reporting</h3>
            <p>Analyze daily, weekly, and monthly sales performance.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faLayerGroup} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Multi-Store Support</h3>
            <p>Manage multiple branches from a centralized dashboard.</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Bell POS streamlines billing, order processing, inventory tracking,
            and performance reporting.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/bell-pos-stores.png" className="rounded-xl shadow-lg" alt="bell pos" />
          <img src="../images/bell-pos-users.png" className="rounded-xl shadow-lg" alt="bell pos" />
          <img src="../images/bell-pos-invoice-holds.png" className="rounded-xl shadow-lg" alt="bell pos" />
          <img src="../images/bell-pos-deals.png" className="rounded-xl shadow-lg" alt="bell pos" />
        </div>
      </section>

      <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            Businesses using Bell POS improved checkout speed, gained better control over inventory,
            and achieved clearer visibility into daily sales performance.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-600 mb-4" />
              <h3 className="font-semibold text-lg">Faster Billing</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-600 mb-4" />
              <h3 className="font-semibold text-lg">Reduced Checkout Time</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartLine} className="text-3xl text-yellow-600 mb-4" />
              <h3 className="font-semibold text-lg">Better Sales Insights</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="flex flex-col items-center py-16 px-8">
        <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
        <div className="flex flex-wrap gap-8 justify-center">
          {technologiesIcon.map((item, index) => (
            <img key={index} src={item} alt="technology" width="90" />
          ))}
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default PosSystem